import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import type { ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';

/**
 * Bouton Astrolabe.
 *
 * Le retour visuel au survol et a l'appui passe par `transform` et des couleurs,
 * jamais par une dimension : un bouton qui grandit pousserait ses voisins et
 * ferait bouger la page (§7, CLS a zero).
 */
const variantesBouton = cva(
  cn(
    'bouton-anime inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[--radius-astro]',
    'font-medium transition-[background-color,border-color,color,transform]',
    'duration-150 ease-out select-none',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ),
  {
    variants: {
      variante: {
        principal: cn(
          'bg-accent text-accent-contraste shadow-astro-basse',
          'hover:bg-accent-clair',
        ),
        secondaire: cn(
          'bg-surface-haute text-texte border border-bordure',
          'hover:bg-surface-survol hover:border-bordure-forte',
        ),
        contour: cn(
          'border border-bordure-forte text-texte bg-transparent',
          'hover:bg-surface-haute hover:border-accent',
        ),
        discret: 'text-texte-doux hover:bg-surface-haute hover:text-texte',
        lien: 'text-accent underline-offset-4 hover:underline',
        danger: cn(
          'bg-danger text-white shadow-astro-basse',
          'hover:brightness-110',
        ),
      },
      taille: {
        sm: 'h-8 px-3 text-xs [&_svg]:size-3.5',
        md: 'h-10 px-4 text-sm [&_svg]:size-4',
        lg: 'h-11 px-6 text-sm [&_svg]:size-4',
        icone: 'size-10 [&_svg]:size-4',
        'icone-sm': 'size-8 [&_svg]:size-3.5',
      },
    },
    defaultVariants: { variante: 'principal', taille: 'md' },
  },
);

export interface ProprietesBouton
  extends ComponentProps<'button'>,
    VariantProps<typeof variantesBouton> {
  /** Rend le composant enfant plutot qu'un `<button>` (pour un `<Link>` par exemple). */
  asChild?: boolean;
  /** Affiche un indicateur et desactive le bouton. */
  enCours?: boolean;
}

export function Bouton({
  className,
  variante,
  taille,
  asChild = false,
  enCours = false,
  disabled,
  children,
  ...reste
}: ProprietesBouton) {
  const Composant = asChild ? Slot : 'button';

  return (
    <Composant
      className={cn(variantesBouton({ variante, taille }), className)}
      disabled={disabled || enCours}
      // Un lecteur d'ecran doit savoir que l'action est en cours, pas seulement le voir.
      aria-busy={enCours || undefined}
      {...reste}
    >
      {enCours ? (
        <>
          <Loader2 className="animate-spin" aria-hidden />
          {children}
        </>
      ) : (
        children
      )}
    </Composant>
  );
}

export { variantesBouton };
